import React, { useState, useMemo } from "react";
import { useData } from "../context/DataContext";

import BalanceCard from "../components/BalanceCard";
import RunwayCard from "../components/RunwayCard";
import IncomeCard from "../components/IncomeCard";
import ExpenseCard from "../components/ExpenseCard";
import RunwayChart from "../components/RunwayChart";
import ExpenseChart from "../components/ExpenseChart";
import RecentTransactions from "../components/RecentTransactions";
import QuickActions from "../components/QuickActions";
import { Airplane, Users } from "@phosphor-icons/react";

const Dashboard: React.FC = () => {
  const { getDashboardData, state, dispatch } = useData();
  const [showSeizedScenario, setShowSeizedScenario] = useState(true);

  const dashboardData = getDashboardData();

  // Memoize calculations to avoid unnecessary re-renders
  const {
    hasTransactions,
    hasRunwayData,
    recentIncomeTransactions,
    recentExpenseTransactions,
    seizedRunwayProjection,
    userPartners,
    activePartnersCount,
  } = useMemo(() => {
    if (!dashboardData) {
      return {
        hasTransactions: false,
        hasRunwayData: false,
        recentIncomeTransactions: [],
        recentExpenseTransactions: [],
        seizedRunwayProjection: [],
        userPartners: [],
        activePartnersCount: 0,
      };
    }

    const hasTransactions =
      dashboardData.recentTransactions &&
      dashboardData.recentTransactions.length > 0;
    const hasRunwayData =
      dashboardData.runwayProjection &&
      dashboardData.runwayProjection.length > 0;

    const recentIncomeTransactions = dashboardData.recentTransactions.filter(
      (t: any) => t.type === "income"
    );

    const recentExpenseTransactions = dashboardData.recentTransactions.filter(
      (t: any) => t.type === "expense"
    );

    // Calculate seized runway projection (no income, only expenses)
    const calculateSeizedRunwayProjection = () => {
      const projections = [];
      let balance = dashboardData.currentBalance;
      const monthlyExpenses = dashboardData.monthlyExpenses;

      // Add current month (month 0)
      projections.push({
        month: 0,
        balance,
        runwayMonths:
          monthlyExpenses > 0 ? balance / monthlyExpenses : Infinity,
      });

      // Calculate future months (no income, only expenses)
      for (let i = 1; i <= 24; i++) {
        const projectedBalance = balance - monthlyExpenses;
        const runwayMonths =
          monthlyExpenses > 0 ? projectedBalance / monthlyExpenses : Infinity;

        projections.push({
          month: i,
          balance: projectedBalance,
          runwayMonths,
        });

        balance = projectedBalance;
      }

      return projections;
    };

    const userPartners = state.partners.filter(
      (p) => p.userId === state.currentUserId
    );
    const activePartnersCount = userPartners.filter((p) => p.isActive).length;

    return {
      hasTransactions,
      hasRunwayData,
      recentIncomeTransactions,
      recentExpenseTransactions,
      seizedRunwayProjection: calculateSeizedRunwayProjection(),
      userPartners,
      activePartnersCount,
    };
  }, [dashboardData, state.partners, state.currentUserId]);

  const handleAddFirstTransaction = () => {
    // Show a helpful message about adding transactions
    alert(
      "You can add transactions through the Income and Expenses pages, or use the 'Add Transaction' button in the header."
    );
  };

  const handleTogglePartner = (partnerId: string) => {
    const partner = state.partners.find((p) => p.id === partnerId);
    if (partner) {
      dispatch({
        type: "UPDATE_PARTNER",
        payload: { ...partner, isActive: !partner.isActive },
      });
    }
  };

  const handleToggleAllPartners = (active: boolean) => {
    state.partners.forEach((partner) => {
      if (partner.userId === state.currentUserId) {
        dispatch({
          type: "UPDATE_PARTNER",
          payload: { ...partner, isActive: active },
        });
      }
    });
  };

  // If no dashboard data, show empty state
  if (!dashboardData) {
    return (
      <main className="space-y-6" role="main" aria-label="Dashboard">
        <section className="card" aria-labelledby="welcome-heading">
          <div className="text-center py-12">
            <Airplane
              className="w-8 h-8 text-gray-500 dark:text-white mx-auto mb-4"
              weight="duotone"
              aria-hidden="true"
            />
            <h1
              id="welcome-heading"
              className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Welcome to Runway
            </h1>
            <p className="text-gray-700 dark:text-gray-300 mb-8 max-w-md mx-auto">
              Start tracking your finances by adding your first transaction.
              We'll help you calculate your financial runway and make informed
              decisions.
            </p>
            <button
              className="btn-primary"
              onClick={handleAddFirstTransaction}
              aria-describedby="welcome-description">
              Add Your First Transaction
            </button>
            <div id="welcome-description" className="sr-only">
              Opens a dialog with instructions on how to add your first
              transaction
            </div>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="space-y-6" role="main" aria-label="Dashboard">
      {/* Partner Management Section */}
      {userPartners.length > 0 && (
        <section
          className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3"
          aria-labelledby="partner-management-heading">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2">
              <Users
                className="w-4 h-4 text-blue-500"
                weight="duotone"
                aria-hidden="true"
              />
              <h2
                id="partner-management-heading"
                className="text-sm font-medium text-blue-900 dark:text-blue-100">
                Partner management
              </h2>
              <span
                className="text-xs text-blue-600 dark:text-blue-300"
                aria-label={`${activePartnersCount} active partners`}>
                ({activePartnersCount} active)
              </span>
            </div>
            <div
              className="flex items-center space-x-1"
              role="group"
              aria-label="Partner management controls">
              <button
                onClick={() => handleToggleAllPartners(false)}
                className="text-xs px-2 py-1 bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-200 rounded hover:bg-red-200 dark:hover:bg-red-800 transition-colors"
                aria-label="Disable all partners">
                Disable All
              </button>
              <button
                onClick={() => handleToggleAllPartners(true)}
                className="text-xs px-2 py-1 bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-200 rounded hover:bg-green-200 dark:hover:bg-green-800 transition-colors"
                aria-label="Enable all partners">
                Enable All
              </button>
            </div>
          </div>

          {/* Individual Partner Toggles */}
          <div
            className="space-y-1"
            role="list"
            aria-label="Individual partner controls">
            {userPartners.map((partner) => (
              <div
                key={partner.id}
                className="flex items-center justify-between text-xs"
                role="listitem">
                <div className="flex items-center space-x-2">
                  <span className="text-blue-700 dark:text-blue-300">
                    {partner.name}
                  </span>
                  <button
                    onClick={() => handleTogglePartner(partner.id)}
                    className={`px-2 py-1 rounded transition-colors ${
                      partner.isActive
                        ? "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-200 hover:bg-green-200 dark:hover:bg-green-800"
                        : "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
                    }`}
                    aria-label={`${partner.name} is ${
                      partner.isActive ? "active" : "inactive"
                    }. Click to toggle.`}
                    aria-pressed={partner.isActive}>
                    {partner.isActive ? "Active" : "Inactive"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Summary Cards */}
      <section
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6"
        aria-label="Financial summary cards">
                    <BalanceCard
              currentBalance={dashboardData.currentBalance}
              assets={dashboardData.assets}
              debts={dashboardData.debts}
              partners={userPartners}
            />
        <RunwayCard
          currentRunway={
            dashboardData.runwayProjection.length > 0
              ? dashboardData.runwayProjection[0].runwayMonths
              : 0
          }
          monthlyIncome={dashboardData.monthlyIncome}
          monthlyExpenses={dashboardData.monthlyExpenses}
          currentBalance={dashboardData.currentBalance}
          assets={dashboardData.assets}
          showSeizedScenario={showSeizedScenario}
          onToggleSeizedScenario={setShowSeizedScenario}
        />
        <IncomeCard
          monthlyIncome={dashboardData.monthlyIncome}
          incomeSources={dashboardData.incomeSources || []}
          recentIncomeTransactions={recentIncomeTransactions}
          partners={userPartners.filter(p => p.isActive)}
        />
        <ExpenseCard
          monthlyExpenses={dashboardData.monthlyExpenses}
          expenseBreakdown={dashboardData.expenseBreakdown}
          recentExpenseTransactions={recentExpenseTransactions}
          partners={userPartners.filter(p => p.isActive)}
        />
      </section>

      {/* Charts Row */}
      {(hasRunwayData || hasTransactions) && (
        <section
          className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6"
          aria-label="Financial charts">
          <div className="lg:col-span-2">
            <RunwayChart
              data={
                showSeizedScenario
                  ? seizedRunwayProjection
                  : dashboardData.runwayProjection
              }
              isSeizedScenario={showSeizedScenario}
            />
          </div>
          <div className="lg:col-span-1">
            <ExpenseChart />
          </div>
        </section>
      )}

      {/* Bottom Row */}
      <section
        className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6"
        aria-label="Recent activity and quick actions">
        <RecentTransactions transactions={dashboardData.recentTransactions} />
        <QuickActions />
      </section>
    </main>
  );
};

export default Dashboard;
